import { FC, useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@features/ui';
import { getLTVDistribution } from '@src/services/reports.ts';
import {
  TReportLTVDistributionRes,
  TLTVDistributionItem,
} from '@src/types/reports';
import { formatCurrency, centsToUSD } from '@lib/utils';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

interface LTVDistributionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  startDate?: string | null;
  endDate?: string | null;
}

export const LTVDistributionDialog: FC<LTVDistributionDialogProps> = ({
  open,
  onOpenChange,
  startDate,
  endDate,
}) => {
  const [data, setData] = useState<TReportLTVDistributionRes | null>();
  const [loading, setLoading] = useState(false);

  const COLORS = [
    '#1890FF',
    '#52C41A',
    '#FAAD14',
    '#F5222D',
    '#722ED1',
    '#13C2C2',
    '#EB2F96',
    '#FA8C16',
  ];

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open, startDate, endDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await getLTVDistribution({
        start_date: startDate || null,
        end_date: endDate || null,
      });

      setData(result);
    } catch (error) {
      console.error('Error fetching LTV distribution:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Customer Lifetime Value Distribution</DialogTitle>
          <DialogDescription>
            Distribution of customers across LTV ranges
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <p className="text-muted-foreground">Loading...</p>
          </div>
        ) : data ? (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-lg bg-card-stat p-4">
                <p className="text-sm text-muted-foreground mb-1">
                  Total Customers
                </p>
                <p className="text-2xl font-semibold">
                  {data.total_customers.toLocaleString()}
                </p>
              </div>
              <div className="rounded-lg bg-card-stat p-4">
                <p className="text-sm text-muted-foreground mb-1">
                  Average LTV
                </p>
                <p className="text-2xl font-semibold">
                  ${formatCurrency(centsToUSD(data.average_ltv), 2)}
                </p>
              </div>
            </div>

            {data.distribution.length > 0 ? (
              <>
                {/* Customer Count Distribution Histogram */}
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold">
                    LTV Distribution (Customer Count)
                  </h3>
                  <div className="h-64 w-full rounded-lg bg-card-stat p-4">
                    <ResponsiveContainer height="100%" width="100%">
                      <BarChart data={data.distribution}>
                        <CartesianGrid stroke="#333" strokeDasharray="3 3" />
                        <XAxis
                          dataKey="range_label"
                          tick={{ fill: '#888', fontSize: 12 }}
                        />
                        <YAxis tick={{ fill: '#888', fontSize: 12 }} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#1a1a1a',
                            border: '1px solid #333',
                            borderRadius: '8px',
                          }}
                          cursor={{ fill: 'transparent' }}
                          formatter={(value: number) => [
                            value.toLocaleString(),
                            'Customers',
                          ]}
                        />
                        <Bar
                          dataKey="customer_count"
                          fill="#1890FF"
                          radius={[8, 8, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Percentage of Customers Pie Chart */}
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold">
                    Customer Distribution by LTV Bucket
                  </h3>
                  <div className="h-96 w-full rounded-lg bg-card-stat p-4">
                    <ResponsiveContainer height="100%" width="100%">
                      <PieChart>
                        <Pie
                          cx="50%"
                          cy="50%"
                          data={data.distribution}
                          dataKey="customer_count"
                          label={(entry) => `${entry.percentage.toFixed(1)}%`}
                          labelLine={{ stroke: '#888' }}
                          nameKey="range_label"
                          outerRadius={120}
                        >
                          {data.distribution.map((_entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={COLORS[index % COLORS.length]}
                            />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#2d2d2d',
                            border: '1px solid #555',
                            borderRadius: '8px',
                            color: '#fff',
                          }}
                          formatter={(value: number) => [
                            value.toLocaleString(),
                            'Customers',
                          ]}
                          itemStyle={{
                            color: '#fff',
                          }}
                          labelStyle={{
                            color: '#fff',
                          }}
                        />
                        <Legend
                          formatter={(value) => {
                            const item = data.distribution.find(
                              (d) => d.range_label === value
                            );

                            return `${value} (${item?.percentage.toFixed(1)}%)`;
                          }}
                          height={36}
                          verticalAlign="bottom"
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Data Table */}
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium">
                          LTV Range
                        </th>
                        <th className="text-right p-3 text-sm font-medium">
                          Customers
                        </th>
                        <th className="text-right p-3 text-sm font-medium">
                          % of Total
                        </th>
                        <th className="text-right p-3 text-sm font-medium">
                          Total Revenue
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {data.distribution.map(
                        (item: TLTVDistributionItem, index: number) => (
                          <tr
                            key={`${item.range_min}-${index}`}
                            className="hover:bg-muted/30"
                          >
                            <td className="p-3 text-sm font-medium">
                              {item.range_label}
                            </td>
                            <td className="p-3 text-sm text-right">
                              {item.customer_count.toLocaleString()}
                            </td>
                            <td className="p-3 text-sm text-right">
                              {item.percentage.toFixed(2)}%
                            </td>
                            <td className="p-3 text-sm text-right font-medium">
                              $
                              {formatCurrency(
                                centsToUSD(item.total_revenue),
                                2
                              )}
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center py-8">
                <p className="text-muted-foreground">
                  No distribution data available
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-center py-8">
            <p className="text-muted-foreground">No data available</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
